import { create } from 'zustand';
import { errorMessage } from '../lib/errors.js';
import {
  type CatalogItem,
  type CatalogItemInput,
  listCatalog,
  createCatalogItem,
  updateCatalogItem,
  deleteCatalogItem,
} from '../api/catalog.js';
import { searchCatalog, sortCatalog } from '../lib/catalog-search.js';
import { currentStore } from './shop.js';
import { otherStore, type Store } from '../lib/store.js';

interface CatalogState {
  items: CatalogItem[];
  loading: boolean;
  /** Le magasin dont `items` est le catalogue — `null` tant que rien n'est chargé.
   * Remplace le booléen `loaded` : « déjà chargé » n'a plus de sens sans dire pour qui. */
  loadedStore: Store | null;
  error: string | null;
  /** Combien d'items vivent dans l'autre magasin, `null` tant qu'on ne l'a pas demandé.
   * Renseigné seulement quand le catalogue affiché est vide (ADR 0011, empty state). */
  otherCount: number | null;
  load: () => Promise<void>;
  loadOtherCount: () => Promise<void>;
  create: (input: Omit<CatalogItemInput, 'store'>) => Promise<CatalogItem>;
  /** `store` est volontairement absent : le backend le refuse (ADR 0011). */
  update: (itemId: string, patch: Partial<Omit<CatalogItemInput, 'store'>>) => Promise<CatalogItem>;
  remove: (itemId: string) => Promise<void>;
  search: (query: string) => CatalogItem[];
}

export const useCatalog = create<CatalogState>((set, get) => ({
  items: [],
  loading: false,
  loadedStore: null,
  error: null,
  otherCount: null,
  load: async () => {
    const store = currentStore();
    set({ items: [], loading: true, error: null, otherCount: null });
    try {
      set({ items: await listCatalog(store), loading: false, loadedStore: store });
    } catch (err) {
      set({ error: errorMessage(err), loading: false });
    }
  },

  /** Le second GET de l'empty state : seulement quand le catalogue affiché est vide,
   * pour pouvoir dire où sont les items plutôt que de laisser croire à une perte. */
  loadOtherCount: async () => {
    try {
      const items = await listCatalog(otherStore(currentStore()));
      set({ otherCount: items.length });
    } catch {
      // L'indice est un confort : s'il échoue, l'empty state reste sans lui.
      set({ otherCount: null });
    }
  },

  create: async (input) => {
    const item = await createCatalogItem({ ...input, store: currentStore() });
    set({ items: sortCatalog([...get().items, item]) });
    return item;
  },
  update: async (itemId, patch) => {
    const updated = await updateCatalogItem(itemId, patch);
    set({
      items: sortCatalog(
        get().items.map((it) => (it.itemId === itemId ? updated : it))
      ),
    });
    return updated;
  },
  remove: async (itemId) => {
    await deleteCatalogItem(itemId);
    set({ items: get().items.filter((it) => it.itemId !== itemId) });
  },
  search: (query) => searchCatalog(get().items, query),
}));
